import type { McpTool } from "@p0security/shared/types/agentic/mcp";

// Listed by the Datadog MCP server (default toolsets) on 2026-09-30. Its
// tools/list needs a token, which P0 does not hold when it syncs a gateway.
export const datadogMcpTools: McpTool[] = [
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Aggregate Events",
    },
    description:
      "Aggregate Datadog events to compute counts, sums, averages, min, max, cardinality, and percentiles (P50, P75, P90, P95, P98, P99), with optional grouping by fields or time intervals. Use this for aggregated analysis such as event counts by source, event frequency over time, or grouped summaries across tags. For raw event inspection, titles/messages, or tag exploration, use search_datadog_events.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        computes: {
          description:
            "Required. Array of aggregation computations to perform. Each compute specifies a field, aggregation function, and output name. Exactly one compute may include a 'sort' field.",
          items: {
            additionalProperties: false,
            properties: {
              aggregation: {
                description:
                  "Aggregation function: SUM, COUNT, AVG, MIN, MAX, CARDINALITY, P50, P75, P90, P95, P98, P99, MEDIAN. Case-insensitive.",
                type: "string",
              },
              field: {
                description:
                  "The field to aggregate on. Use '*' for COUNT aggregation.",
                type: "string",
              },
              output: {
                description: "Name for the output field in the response.",
                type: "string",
              },
              sort: {
                description:
                  "Optional sort direction: asc or desc, case-insensitive. Only one compute may specify sort.",
                pattern: "^([aA][sS][cC]|[dD][eE][sS][cC])$",
                type: "string",
              },
            },
            required: ["field", "aggregation", "output"],
            type: "object",
          },
          type: "array",
        },
        from: {
          description:
            "Optional. Start of the time window. Accepts ISO 8601, Unix epoch ms, or relative format (e.g. 'now-15m', 'now-1h'). Defaults to 'now-1h'.",
          type: "string",
        },
        group_by: {
          additionalProperties: false,
          description:
            "Optional. Grouping configuration. Specify 'fields' for field-based grouping and/or 'interval' for time-based histograms (in milliseconds).",
          properties: {
            fields: {
              description: "Field names to group by (e.g. 'source', 'host').",
              items: {
                type: "string",
              },
              type: "array",
            },
            histogram: {
              additionalProperties: false,
              description:
                "Bounded numeric histogram grouping: {field, interval, min, max}.",
              properties: {
                field: {
                  type: "string",
                },
                interval: {
                  type: "integer",
                },
                max: {
                  type: "integer",
                },
                min: {
                  type: "integer",
                },
              },
              required: ["field", "interval", "min", "max"],
              type: "object",
            },
            interval: {
              description:
                "Histogram interval in milliseconds for time-based grouping (e.g. 3600000 for 1-hour buckets).",
              type: "integer",
            },
            limit: {
              description:
                "Maximum number of buckets for field grouping. Default is 1000.",
              type: "integer",
            },
          },
          type: "object",
        },
        max_tokens: {
          default: 10000,
          description:
            "Max tokens in the response. Hard cap: 20000 (values above are clamped, not rejected).",
          minimum: 0,
          type: ["integer", "string"],
        },
        query: {
          description:
            "Required. Search query to filter events before aggregation. Use Datadog query syntax. Examples: 'source:resource_changes', 'env:prod', '*' for all events.",
          type: "string",
        },
        start_at: {
          default: 0,
          description: "Offset for pagination.",
          minimum: 0,
          type: ["integer", "string"],
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        to: {
          description:
            "Optional. End of the time window. Accepts ISO 8601, Unix epoch ms, or relative format (e.g. 'now'). Defaults to 'now'.",
          type: "string",
        },
      },
      required: ["query", "computes", "telemetry"],
      type: "object",
    },
    name: "aggregate_events",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Aggregate RUM Events",
    },
    description:
      "Aggregate Datadog RUM events to compute counts, sums, averages, min, max, cardinality, and percentiles (P50, P75, P90, P95, P98, P99), with optional grouping by fields or time intervals. Use this for aggregated analysis of RUM data such as session counts over time, error counts by page, or p95 loading times by browser or URL path. For raw event inspection or attribute discovery, use search_datadog_rum_events.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        computes: {
          description:
            "Required. Array of aggregation computations to perform. Each compute specifies a field, aggregation function, and output name. Exactly one compute may include a 'sort' field.",
          items: {
            additionalProperties: false,
            properties: {
              aggregation: {
                description:
                  "Aggregation function: SUM, COUNT, AVG, MIN, MAX, CARDINALITY, P50, P75, P90, P95, P98, P99, MEDIAN. Case-insensitive.",
                type: "string",
              },
              field: {
                description:
                  "The field to aggregate on. Use '*' for COUNT aggregation.",
                type: "string",
              },
              output: {
                description: "Name for the output field in the response.",
                type: "string",
              },
              sort: {
                description:
                  "Optional sort direction. Only one compute may specify sort.",
                enum: ["asc", "desc"],
                type: "string",
              },
            },
            required: ["field", "aggregation", "output"],
            type: "object",
          },
          type: "array",
        },
        from: {
          description:
            "Optional. Start of the time window. Accepts ISO 8601, Unix epoch ms, or relative format (e.g. 'now-1h', 'now-1d', 'now-7d', 'now-30d'). Defaults to 'now-7d'.",
          type: "string",
        },
        group_by: {
          additionalProperties: false,
          description:
            "Optional. Grouping configuration. Specify 'fields' for field-based grouping and/or 'interval' for time-based histograms (in milliseconds).",
          properties: {
            fields: {
              description:
                "Field names to group by (e.g. '@browser.name', '@geo.country'). Events missing the grouping field are dropped, so summed bucket counts may be less than an ungrouped COUNT.",
              items: {
                type: "string",
              },
              type: "array",
            },
            interval: {
              description:
                "Histogram interval in milliseconds for time-based grouping (e.g. 3600000 for 1-hour buckets). Each bucket carries a `timestamp` ISO 8601 field marking its start.",
              type: "integer",
            },
            limit: {
              description:
                "Maximum number of buckets for field grouping. Default is 1000.",
              type: "integer",
            },
          },
          type: "object",
        },
        max_tokens: {
          default: 10000,
          description: "Max tokens in the response.",
          minimum: 0,
          type: "number",
        },
        query: {
          description:
            "Required. Search query to filter events before aggregation. Use Datadog query syntax. RUM timing fields (e.g. `@view.loading_time`, `@action.loading_time`, `@resource.duration`) are in nanoseconds (1s = 1e9). Examples: '@type:error', '@type:view @view.loading_time:>5000000000' (>5s), '*' for all events. IMPORTANT: free-text search uses full-word matching \u2014 dots (.) are NOT word separators (`foo.bar` is one token, `bar` alone matches nothing). Wrap partial terms in wildcards or target the attribute: `@error.message:*bar*`.",
          type: "string",
        },
        start_at: {
          default: 0,
          description: "Offset for pagination.",
          minimum: 0,
          type: "number",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        to: {
          description:
            "Optional. End of the time window. Accepts ISO 8601, Unix epoch ms, or relative format (e.g. 'now'). Defaults to 'now'.",
          type: "string",
        },
      },
      required: ["query", "computes", "telemetry"],
      type: "object",
    },
    name: "aggregate_rum_events",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Aggregate Spans",
    },
    description:
      "Aggregate Datadog APM spans to compute counts, sums, averages, min, max, cardinality, and percentiles (P50, P75, P90, P95, P98, P99), with optional grouping by fields or time intervals. Use this for aggregated analysis such as request counts over time, p95 duration by service or resource, or error counts grouped by endpoint or status code. For raw span inspection or to discover fields to group by or aggregate on, first use search_datadog_spans and optionally request custom_attributes.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        computes: {
          description:
            "Required. Array of aggregation computations to perform. Each compute specifies a field, aggregation function, and output name.",
          items: {
            additionalProperties: false,
            properties: {
              aggregation: {
                description:
                  "Aggregation function: SUM, COUNT, AVG, MIN, MAX, CARDINALITY, P50, P75, P90, P95, P98, P99, MEDIAN. Case-insensitive.",
                type: "string",
              },
              field: {
                description:
                  "The field to aggregate on. Use '*' for COUNT aggregation, or span attributes like '@duration' for latency computes. Bare 'duration' is invalid; use '@duration' instead.",
                type: "string",
              },
              output: {
                description: "Name for the output field in the response.",
                type: "string",
              },
            },
            required: ["field", "aggregation", "output"],
            type: "object",
          },
          type: "array",
        },
        from: {
          description:
            "Start of the time range. Accepts ISO 8601 (e.g. '2024-01-01T00:00:00Z' or '2024-01-01'), Unix timestamp (seconds or milliseconds), or relative (e.g. 'now-1h', 'now-7d', 'now'). Default: now-1h.",
          type: "string",
        },
        group_by: {
          additionalProperties: false,
          description:
            "Optional. Grouping configuration. Specify 'fields' for field-based grouping and/or 'interval' for time-based histograms (in milliseconds).",
          properties: {
            fields: {
              description:
                "Field names to group by (e.g. 'service', 'resource_name', '@http.status_code').",
              items: {
                type: "string",
              },
              type: "array",
            },
            interval: {
              description:
                "Histogram interval in milliseconds for time-based grouping (e.g. 3600000 for 1-hour buckets).",
              type: "integer",
            },
            limit: {
              description:
                "Maximum number of buckets for field grouping. Default is 1000.",
              type: "integer",
            },
          },
          type: "object",
        },
        max_tokens: {
          default: 10000,
          description: "Max tokens in the response.",
          minimum: 0,
          type: "number",
        },
        query: {
          description:
            "Required. Search query to filter spans before aggregation. Use Datadog query syntax. Examples: 'service:web-store', 'resource_name:GET /api/users', '@http.status_code:500', '*' for all spans.",
          type: "string",
        },
        sort: {
          additionalProperties: false,
          description:
            "Optional. Sorts the result by one compute from the 'computes' array. Defaults to sorting descending by the first compute.",
          properties: {
            compute_index: {
              description:
                "Zero-based index into 'computes' identifying which compute to sort by.",
              minimum: 0,
              type: "integer",
            },
            order: {
              description: "Sort direction.",
              enum: ["asc", "desc"],
              type: "string",
            },
          },
          required: ["compute_index", "order"],
          type: "object",
        },
        start_at: {
          default: 0,
          description: "Offset for pagination.",
          minimum: 0,
          type: "number",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        to: {
          description:
            "End of the time range. Accepts ISO 8601 (e.g. '2024-01-01T00:00:00Z' or '2024-01-01'), Unix timestamp (seconds or milliseconds), or relative (e.g. 'now-1h', 'now-7d', 'now'). Default: now.",
          type: "string",
        },
      },
      required: ["query", "computes", "telemetry"],
      type: "object",
    },
    name: "aggregate_spans",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Analyze Datadog Logs",
    },
    description:
      "Analyze Datadog logs using SQL. Runs against a virtual 'logs' table filtered by your search query. Good for aggregations, counts, group-bys, or peeking at recent logs with LIMIT. To discover custom attributes for extra_columns, first call search_datadog_logs with extra_fields. If a query times out, try a shorter time range. MUST load the datadog/ddsql skill before calling this tool.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        extra_columns: {
          description:
            "Extend the logs table with additional typed columns. To discover available attributes, call search_datadog_logs with extra_fields (e.g. ['*'] or ['http*']), then declare them here. Key translation from search response keys to extra_columns names: strip the bucket prefix and prepend '@' \u2014 `custom.X` \u2192 `{name: \"@X\"}`, `attributes.X` \u2192 `{name: \"@X\"}`, `error.X` \u2192 `{name: \"@error.X\"}`; tags from the `tags:` list use bare names without '@' \u2014 e.g. tag `team:foo` \u2192 `{name: \"team\"}`. Never pass the raw response-prefixed form (e.g. never `custom.X` or `attributes.X`). Supported types: varchar, bigint, decimal, boolean, timestamp, array<varchar>, array<bigint>, array<decimal>, array<boolean>, array<timestamp>.",
          items: {
            additionalProperties: false,
            properties: {
              name: {
                description: "Column name in the SQL virtual table.",
                type: "string",
              },
              type: {
                enum: [
                  "varchar",
                  "bigint",
                  "decimal",
                  "boolean",
                  "timestamp",
                  "array<varchar>",
                  "array<bigint>",
                  "array<decimal>",
                  "array<boolean>",
                  "array<timestamp>",
                ],
                type: "string",
              },
            },
            required: ["name", "type"],
            type: "object",
          },
          type: "array",
        },
        filter: {
          default: "*",
          description:
            "Datadog search query to filter logs before SQL analysis. When relevant services are known, prefer filtering by service name or prefix. Broad queries are fine for discovery or cross-service questions. Use key:value for tags/facets (e.g. 'service:nginx', 'env:prod'), '@' prefix for raw attributes (e.g. '@http.status_code:[400 TO 499]'). Examples: 'service:nginx status:error', 'env:(production OR test) AND -version:beta', 'service:web* foo'. Tip: '(source:foo OR service:foo)' if unsure which field. For severe issues: 'status:(critical OR alert OR emergency)'.",
          type: "string",
        },
        from: {
          default: "now-1h",
          description:
            "Start of time window. ISO 8601, Unix epoch ms, or relative ('now-1h').",
          type: "string",
        },
        indexes: {
          description:
            "Restrict to specific log indexes (e.g. ['main', 'pci']). Default: ['*'] (all).",
          items: {
            type: "string",
          },
          type: "array",
        },
        max_tokens: {
          default: 10000,
          description: "Max tokens in the response.",
          minimum: 0,
          type: "number",
        },
        sql_query: {
          description:
            "SQL against the 'logs' table. Examples: 'SELECT count(*) FROM logs', 'SELECT service, count(*) FROM logs GROUP BY service ORDER BY count(*) DESC', 'SELECT status, count(*) FROM logs GROUP BY status', 'SELECT DATE_TRUNC(\\'hour\\', timestamp) as hour, count(*) FROM logs GROUP BY DATE_TRUNC(\\'hour\\', timestamp)'. Default columns: timestamp (timestamp), host (string), service (string), env (string), version (string), status (string), message (string); add more via extra_columns. DDSQL (Datadog SQL) is a PostgreSQL subset: every non-aggregated SELECT column must appear in GROUP BY, SELECT aliases cannot be reused in WHERE/GROUP BY/HAVING (repeat the full expression instead, e.g., GROUP BY DATE_TRUNC('hour', timestamp) not GROUP BY hour), and only declared table columns may be referenced. Use the -> operator or json_extract_path_text (cast as needed) for JSON access. Avoid unsupported constructs like ANY(), ->>, QUALIFY, information_schema, and current_timestamp. Column names containing special characters like '@' must be quoted (e.g., SELECT \"@foo\" FROM logs). TIME FILTERING: the virtual table already contains only events within the from/to time window \u2014 never add WHERE timestamp >= ... in SQL. INTERVAL and CURRENT_TIMESTAMP are not supported and will cause query errors.",
          type: "string",
        },
        start_at: {
          default: 0,
          description: "Offset for pagination.",
          minimum: 0,
          type: "number",
        },
        storage_tier: {
          description:
            "Storage type: 'indexes' (default, hot only), 'flex_and_indexes' (Flex + hot). online-archives and cloudprem not supported.",
          enum: ["indexes", "flex_and_indexes", "flex"],
          type: "string",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        to: {
          default: "now",
          description: "End of time window. Same formats as 'from'.",
          type: "string",
        },
      },
      required: ["sql_query", "telemetry"],
      type: "object",
    },
    name: "analyze_datadog_logs",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Ask Widget Expert",
    },
    description:
      "Required first step for creating or editing a Datadog dashboard or notebook widget. Before constructing widget JSON, describe the user's goal, the question the widget should answer, the intended data source, and any existing widget or validation error. The expert returns a concise how-to guide \u2014 widget type recommendations, required fields, schema patterns, and an annotated example \u2014 that you use to build the widget definition yourself. The expert has deep knowledge of all widget types and schemas but does NOT have access to real telemetry, live data, or the user's environment.\n\nAny examples in the response use PLACEHOLDER values for queries. You are responsible for discovering real metric names, log queries, or APM resource names through data exploration tools and substituting them \u2014 do not copy example queries directly into a widget without first validating them against real data.\n\nThe expert is useful at any stage: choosing a widget type, understanding schema requirements, or debugging an invalid widget. Continue with the returned session_id for follow-up questions and validation errors. Repeat the same resource value on every follow-up call.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        question: {
          description:
            'A question about Datadog widgets.\n\nExamples:\n- "What type of widget is best for showing how different services interact with each other?"\n- "How would I build it?" (follow-up, passing session_id from the previous call)\n- "I want to rank the top 10 slowest API endpoints by p99 latency \u2014 what widget type should I use?"\n- "What fields does a timeseries widget need to show p95 latency grouped by service?"\n- "Help me debug this failing request: <failing request or field and exact error>"\n\nKeep the question under 2,000 characters. For validation recovery, include only the failing request or field rather than the entire widget definition.',
          maxLength: 2000,
          type: "string",
        },
        resource: {
          description:
            'Use "notebook" when building widgets for a Datadog notebook. Defaults to "dashboard". Repeat the same value on every follow-up call.',
          enum: ["dashboard", "notebook"],
          type: "string",
        },
        session_id: {
          description:
            "Session ID returned by a previous ask_widget_expert call. Pass it to continue a multi-turn conversation and give the expert context from earlier questions.",
          type: "string",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
      },
      required: ["question", "telemetry"],
      type: "object",
    },
    name: "ask_widget_expert",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get Change Stories",
    },
    description:
      "Retrieve change events for an APM service over a time range. Use to correlate changes with performance issues, errors or incidents.\n\nTracked Change types: deployment (code/version), feature_flag, traffic_anomaly, watchdog (Datadog anomaly detection), kubernetes (k8s deployment manifest updates), scale (manual k8s scale events), crashloopbackoff (k8s crashloops), database (DB schema or setting changes), schema (data stream schemas only; not DB/API schema), configuration (limited to specific tracked sources \u2014 absence does not imply no config changed)",
    inputSchema: {
      additionalProperties: false,
      properties: {
        context: {
          description: "Why you are calling this tool.",
          type: "string",
        },
        end_ts: {
          description: "End of time range (ISO 8601).",
          type: "string",
        },
        env: {
          description: "Environment (e.g. 'prod'). Empty = all environments.",
          type: "string",
        },
        filter_tags: {
          description:
            "key:value filter pairs (e.g. 'datacenter:us1.prod.dog').",
          items: {
            type: "string",
          },
          type: "array",
        },
        max_tokens: {
          description: "Max response tokens (default 10000).",
          type: "integer",
        },
        service_name: {
          description: "Service name.",
          type: "string",
        },
        start_ts: {
          description:
            "Start of time range (ISO 8601, e.g. '2023-12-21T00:00:00Z').",
          type: "string",
        },
        story_types: {
          description: "Types to include; empty = all.",
          items: {
            enum: [
              "deployment",
              "feature_flag",
              "traffic_anomaly",
              "kubernetes",
              "scale",
              "crashloopbackoff",
              "configuration",
              "database",
              "schema",
              "watchdog",
            ],
            type: "string",
          },
          type: "array",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
      },
      required: ["service_name", "start_ts", "end_ts", "telemetry"],
      type: "object",
    },
    name: "get_change_stories",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get Dashboard",
    },
    description:
      "Retrieves a custom or integration Datadog dashboard by ID, returning its title, description, tags, widgets, and template variables. Use search_datadog_dashboards first to find dashboard IDs.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        dashboard_id: {
          description:
            "The custom dashboard short ID (e.g., 'ps7-mn3-kwf') or numeric integration dashboard ID (e.g., '30584').",
          type: "string",
        },
        include_widgets: {
          description:
            "Whether to include widgets in the response. Defaults to true. Set to false for a lighter response with only dashboard metadata.",
          type: "boolean",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        template_variable_values: {
          description:
            "Optional template variable selections to apply to the returned Datadog UI link. These values affect only the link and do not modify the dashboard.",
          items: {
            additionalProperties: false,
            properties: {
              name: {
                description: "Dashboard template variable name.",
                type: "string",
              },
              values: {
                description:
                  "Values to select for this variable in the returned UI link.",
                items: {
                  type: "string",
                },
                type: "array",
              },
            },
            required: ["name", "values"],
            type: "object",
          },
          type: "array",
        },
      },
      required: ["dashboard_id", "telemetry"],
      type: "object",
    },
    name: "get_datadog_dashboard",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get Incident",
    },
    description:
      "Get detailed information about a specific Datadog incident by ID, including status, severity, timeline, associated users, and attachments.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        incident_id: {
          description: "Incident ID \u2014 a number (e.g. 1239) or UUID.",
          type: "string",
        },
        include_attachments: {
          description:
            "If true, includes postmortem link and/or external links.",
          type: "boolean",
        },
        include_timeline: {
          description:
            "If true, includes the incident timeline with comments and status changes.",
          type: "boolean",
        },
        max_tokens: {
          description: "Max tokens in the response. Default: 10000.",
          type: "number",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        timeline_from: {
          description:
            "Start of the time range. Accepts ISO 8601 (e.g. '2024-01-01T00:00:00Z' or '2024-01-01'), Unix timestamp (seconds or milliseconds), or relative (e.g. 'now-1h', 'now-7d', 'now').",
          type: "string",
        },
        timeline_to: {
          description:
            "End of the time range. Accepts ISO 8601 (e.g. '2024-01-01T00:00:00Z' or '2024-01-01'), Unix timestamp (seconds or milliseconds), or relative (e.g. 'now-1h', 'now-7d', 'now').",
          type: "string",
        },
      },
      required: ["incident_id", "telemetry"],
      type: "object",
    },
    name: "get_datadog_incident",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get Metrics",
    },
    description:
      "Query metrics data from Datadog. Use response_format='timeseries' (default) to get time-indexed data points for graphs and trend analysis. Use response_format='scalar' to get a single aggregated value per group, useful for current state, summaries, and comparisons. For response_format='scalar', use structured query objects; the aggregator field controls how the time window collapses to a single value and defaults to avg if omitted. The query prefix, such as avg: or sum:, is not the scalar aggregator field. Supports multiple queries in one call plus formula expressions. Query syntax: grouping must come before modifiers, e.g. 'sum:trace.http.request.errors{env:prod} by {service}.as_count()', not '.as_count() by {service}'. Within {...}, do not mix comma-separated tag filters with boolean filters; if any top-level filter uses AND, OR, NOT, or IN, join top-level filters with explicit AND and parenthesize OR groups, e.g. '{(service:api OR service:worker) AND datacenter:us1.prod.dog}', not '{service:api OR service:worker,datacenter:us1.prod.dog}'.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        formulas: {
          description:
            "Optional. Formula expressions referencing queries by name. If no name was set, queries are named 'query0', 'query1', etc. Examples: 'query0 + query1', '100 * (query0 / query1)', 'anomalies(query0, \"basic\", 2)', 'top(query0, 10, \"mean\", \"desc\")'.",
          items: {
            type: "string",
          },
          type: "array",
        },
        from: {
          description:
            "Start time. ISO 8601 ('2023-01-01T00:00:00Z'), Unix timestamp (seconds or ms), or relative ('now-1h', 'now-1d'). Default: 1 hour ago.",
          type: "string",
        },
        interval: {
          description:
            "Time interval in milliseconds for timeseries downsampling. Automatically increased when the time range and query count would exceed the metrics API limit of 45,000 epochs. Default: auto. Not used for scalar queries.",
          type: "number",
        },
        max_tokens: {
          description:
            "Max tokens in the response. Hard cap: 20000 (values above are clamped, not rejected).",
          type: "number",
        },
        queries: {
          description:
            "Array of metric queries. Each element can be a raw Datadog query string (e.g. 'avg:system.cpu.user{env:prod} by {host}') or a structured object with metric_name, space_aggregator, and other fields. Use raw strings only for timeseries/default output when you already have a query from a dashboard or notebook. For response_format='scalar', prefer structured objects with an explicit aggregator field; if aggregator is omitted it defaults to avg.",
          items: {
            oneOf: [
              {
                description:
                  "A raw Datadog query string, e.g. 'avg:system.cpu.user{env:prod} by {host}'. Use for timeseries/default output when you already have a formatted query from a dashboard, notebook, or monitor. For response_format='scalar', prefer structured objects so you can set the aggregator field explicitly.",
                type: "string",
              },
              {
                additionalProperties: false,
                properties: {
                  aggregator: {
                    description:
                      "Optional for response_format='scalar'. How to reduce all data points in the time window to a single value. Options: avg, sum, min, max, last. Defaults to avg if omitted. This is separate from the query prefix; avg:system.cpu.user{*} still needs aggregator:'avg' for scalar output.",
                    enum: ["avg", "sum", "min", "max", "last"],
                    type: "string",
                  },
                  filters: {
                    description:
                      "Optional. Tag filters to scope the query. Each element is 'tag:value', e.g. 'env:prod', 'host:web-1', 'region:us-east-1'. Defaults to all sources if omitted.",
                    items: {
                      type: "string",
                    },
                    type: "array",
                  },
                  group_by: {
                    description:
                      "Optional. Tag keys to split results by, e.g. ['host', 'env']. Each unique combination becomes a separate result row/series.",
                    items: {
                      type: "string",
                    },
                    type: "array",
                  },
                  metric_name: {
                    description:
                      "Required (unless 'query' is set). The metric name, e.g. 'system.cpu.user', 'redis.info.latency_ms'. Use search_datadog_metrics to find available metrics.",
                    type: "string",
                  },
                  modifier: {
                    description:
                      "Optional. Query-level function suffix. Options: as_count, as_rate, per_second. E.g. use 'as_count' for counter metrics.",
                    enum: ["as_count", "as_rate", "per_second"],
                    type: "string",
                  },
                  name: {
                    description:
                      "Optional. Identifier for referencing this query in formulas, e.g. 'cpu'. Defaults to 'query0', 'query1', etc.",
                    type: "string",
                  },
                  query: {
                    description:
                      "A raw Datadog query string, e.g. 'avg:system.cpu.user{env:prod} by {host}'. When set, metric_name and space_aggregator are not needed. For scalar responses, put the raw query here and also set aggregator and optionally name.",
                    type: "string",
                  },
                  rollup_interval: {
                    description:
                      "Optional. Rollup bucket size in seconds, e.g. 3600 for 1-hour buckets (timeseries only). Used together with rollup_method.",
                    type: "integer",
                  },
                  rollup_method: {
                    description:
                      "Optional. Time-aggregation method for downsampling within the time window (timeseries only). Options: avg, sum, min, max, count.",
                    enum: ["avg", "sum", "min", "max", "count"],
                    type: "string",
                  },
                  space_aggregator: {
                    description:
                      "Required (unless 'query' is set). How to combine values across matching sources. Options: avg, sum, min, max, p50, p75, p90, p95, p99.",
                    enum: [
                      "avg",
                      "sum",
                      "min",
                      "max",
                      "p50",
                      "p75",
                      "p90",
                      "p95",
                      "p99",
                    ],
                    type: "string",
                  },
                },
                type: "object",
              },
            ],
          },
          type: "array",
        },
        raw_data: {
          description:
            "Optional. For timeseries only. true=CSV raw data, false=binned stats (20 time buckets). Auto-selected if omitted.",
          type: "boolean",
        },
        response_format: {
          description:
            "Optional. 'timeseries' (default): returns time-indexed data points per group, suitable for trend analysis and graphs. 'scalar': returns one aggregated value per group for the full time window, suitable for current-state summaries and comparisons. For scalar, set aggregator on each query object to control how the time window collapses to a single value (defaults to avg if omitted).",
          type: "string",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        to: {
          description: "End time. Same formats as 'from'. Default: now.",
          type: "string",
        },
        use_cloud_cost: {
          description:
            "Set true to query Cloud Cost Management data instead of regular metrics. Cloud cost queries require space_aggregator='sum', rollup_method='sum', and rollup_interval=86400 (daily) or 2592000 (monthly) \u2014 no other aggregator is supported. Examples: {metric_name: 'all.cost', space_aggregator: 'sum', rollup_method: 'sum', rollup_interval: 86400}, {metric_name: 'all.cost', space_aggregator: 'sum', group_by: ['providername'], rollup_method: 'sum', rollup_interval: 86400}, {metric_name: 'all.cost', space_aggregator: 'sum', filters: ['service:abc'], rollup_method: 'sum', rollup_interval: 86400}, {metric_name: 'aws.cost.net.amortized.shared.resources.allocated', space_aggregator: 'sum', filters: ['dd_resource_key:arn:aws:ec2:us-east-1:1234:instance/i-5678'], rollup_method: 'sum', rollup_interval: 86400}.",
          type: "boolean",
        },
      },
      required: ["queries", "telemetry"],
      type: "object",
    },
    name: "get_datadog_metric",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get Metric Context",
    },
    description:
      "Get metadata (description, type, unit, integration), available tags/dimensions, and optionally related assets for a metric. Use this tool to explore a metric before you query it. Set use_cloud_cost=true for Cloud Cost Management metrics.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        include_related_assets: {
          default: false,
          description:
            "If true, includes dashboards, monitors, notebooks, and SLOs that use this metric. Not supported for cloud cost metrics.",
          type: "boolean",
        },
        include_tag_values: {
          default: false,
          description:
            "If true, returns tags grouped by key with values ({'env': ['prod', 'staging']}). If false (default), returns only tag keys (['env', 'service', 'host']).",
          type: "boolean",
        },
        max_tokens: {
          default: 10000,
          description:
            "Max tokens in the response. Hard cap: 20000 (values above are clamped, not rejected).",
          type: "number",
        },
        metric_name: {
          description:
            "Metric name. Examples: 'system.cpu.user', 'redis.info.latency_ms'. Cloud cost examples: 'all.cost', 'aws.cost.amortized', 'azure.cost.actual', 'gcp.cost.amortized'.",
          type: "string",
        },
        scope_tags: {
          description:
            "Tags to scope the metric before retrieving tags. Example: ['env:prod', 'region:us-east-1'] returns tags only from matching hosts.",
          items: {
            type: "string",
          },
          type: "array",
        },
        start_at: {
          default: 0,
          description: "Offset for pagination.",
          type: "number",
        },
        tag_filter: {
          description:
            "Substring filter for tags. Example: 'prod' returns 'env:prod', 'service:prod-api', etc.",
          type: "string",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        use_cloud_cost: {
          description:
            "Set true for Cloud Cost Management metrics. Examples: 'all.cost', 'aws.cost.amortized', 'aws.cost.net.amortized.shared.resources.allocated', 'azure.usage', 'gcp.cost.ondemand'.",
          type: "boolean",
        },
        window: {
          description:
            "Lookback window in seconds for tag data. Default: 14400 (4h), min: 14400, max: 2592000 (30d). Cloud cost uses fixed 7-day lookback.",
          type: "number",
        },
      },
      required: ["metric_name", "telemetry"],
      type: "object",
    },
    name: "get_datadog_metric_context",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get Notebook",
    },
    description:
      "Retrieve information about a specific Datadog notebook by ID. This tool provides details including name, status, and associated author. The ID can also be extracted from a URL. The ID will be the last component, for example, <host>/notebook/<ID>.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        include_comments: {
          default: false,
          description:
            "When true, fetch and include all comments for this notebook in the response. Comments are returned as a flat list; reply comments reference their parent via `parent_id`. Comments take priority over cells in the token budget.",
          type: "boolean",
        },
        max_tokens: {
          default: 10000,
          description: "Max tokens in the response.",
          type: "number",
        },
        notebook_id: {
          description:
            "The numeric ID of the notebook (e.g., '12345678'), or a notebook URL.",
          type: "string",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
      },
      required: ["notebook_id", "telemetry"],
      type: "object",
    },
    name: "get_datadog_notebook",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get Trace",
    },
    description:
      "Retrieve a trace by trace ID from Datadog APM. This tool fetches all spans within a specific trace by default, providing detailed information about the request flow, timing, and service interactions. For large traces or to retrieve a summarized trace, set only_service_entry_spans=true to get a hierarchical condensed view that shows service boundaries, collapsing internal operations. The summarized view will indicate expandable spans with hidden_child_spans_count. The response includes a trace_deep_link_url in metadata that links directly to the trace flamegraph in the Datadog UI.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        expand_span_id: {
          description:
            "Optional. Span ID to expand and show its hidden children. Use this when a span has hidden_child_spans_count > 0 to drill deeper into that span's operations. Pass the span's span_id value to see its internal service spans that were pruned in the initial summary. Only applicable when only_service_entry_spans=true.",
          type: "string",
        },
        extra_fields: {
          description:
            "Optional. Additional meta or metrics tags to include beyond the default diagnostic fields (error.*, http.*, span.kind, rpc.*, grpc.*, peer.*, issue.*, _dd.issue.*). Attributes/tags matching any of the provided patterns will be included alongside the defaults. Supports wildcards (e.g. 'db.*' to add database tags, 'experiments*' for experiment tags).",
          items: {
            type: "string",
          },
          type: "array",
        },
        include_path: {
          description:
            "Optional. Array of strings. Each element is a 'key:value' pair used to filter spans; only matching spans and their ancestor/descendant spans are included in the trace. Can filter on service, resource_name, or meta attributes. Example: ['service:spans-public-api', 'resource_name:/query', 'span.kind:server']. Only applicable when only_service_entry_spans=true.",
          items: {
            type: "string",
          },
          type: "array",
        },
        max_tokens: {
          description: "Max tokens in the response. Default: 10000.",
          type: "number",
        },
        only_service_entry_spans: {
          description:
            "Optional. If true, retrieve a summarized trace that collapses same-service chains so only service entry/exit spans remain, reducing noise from internal service calls. ",
          type: "boolean",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        trace_id: {
          description:
            "Required. The trace ID to retrieve. Must be either 32 lowercase hex characters (e.g. '0123456789abcdef0123456789abcdef') or 1-39 decimal digits. Do not include dashes or uppercase letters.",
          type: "string",
        },
      },
      required: ["trace_id", "telemetry"],
      type: "object",
    },
    name: "get_datadog_trace",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Datadog Skills",
    },
    description:
      "List available Datadog skill guides. Skills document how to use Datadog's tools: the right attributes, query syntax, and common pitfalls. Call this before load_datadog_skill whenever you are not already certain of the exact skill name; skill names are not predictable from topic words. Use query for fuzzy search across name and description (results are ranked). Use include_header=true to see one-line summaries alongside names. Load skills proactively when starting work in a relevant Datadog domain, not after errors.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        include_header: {
          description:
            "If true, include description and related skills alongside each name. Default false.",
          type: "boolean",
        },
        query: {
          description:
            "Optional fuzzy search query to filter skills by name, description, or toolset.",
          type: "string",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
      },
      required: ["telemetry"],
      type: "object",
    },
    name: "list_datadog_skills",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Load Datadog Skill",
    },
    description:
      "Load a Datadog skill guide before using related Datadog tools. Skills improve query quality by documenting the right attributes, syntax, and common pitfalls. Skill names depend on the visible toolsets; if you do not already know the exact skill name from a prior list_datadog_skills response, call list_datadog_skills first and do not guess names from topic keywords. Set header_only=true to preview a skill's summary, related skills, and bundled resources. Set resource_path to load a specific bundled reference.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        header_only: {
          description:
            "If true, return skill name, description, related skills, and available bundled resources instead of full content. Default false.",
          type: "boolean",
        },
        resource_path: {
          description:
            "Load a specific bundled resource file instead of the main skill content. Use header_only=true to discover available resource paths first (e.g. 'references/span-attributes.md').",
          type: "string",
        },
        skill_name: {
          description:
            "The exact visible skill name to load as returned by list_datadog_skills.",
          type: "string",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
      },
      required: ["skill_name", "telemetry"],
      type: "object",
    },
    name: "load_datadog_skill",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Dashboards",
    },
    description:
      "List and retrieve information about Datadog dashboards. This tool helps discover available dashboards, their IDs, titles, and underlying queries. Use this tool when you need to find specific dashboards, get an overview of all dashboards in your Datadog account, or find important logs+metrics queries in dashboards.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        include_description: {
          description: "Include the dashboard description.",
          type: "boolean",
        },
        include_template_variables: {
          description: "Include information about template variables.",
          type: "boolean",
        },
        max_queries_per_dashboard: {
          description:
            "Optional. Maximum number of queries to return per dashboard. Set to a higher value if you want to look up queries in dashboards.",
          type: "integer",
        },
        max_tokens: {
          description:
            "Optional. Maximum number of tokens to include in the response.",
          type: "integer",
        },
        query: {
          description:
            "Optional. Search query to filter dashboards. Supports:\n\u2022 Dashboard ID: 'id:ex2-43q-fiz'\n\u2022 Dashboard name: 'title:Redis', 'title:\"My Service Migration\"'\n\u2022 Widget title: 'widgets.title:\"CPU Usage\"'\n\u2022 Widget type: 'widgets.type:slo'\n\u2022 Widget metric: 'widgets.metrics:system.cpu.user'\n\u2022 User handle: 'author.handle:john.doe@sample.com'\n\u2022 Team tags: 'team:backend', 'team:frontend'\u2022 Boolean expressions with 'AND', 'OR': (team:backend OR team:frontend) AND is_shared:true\n\u2022 Mixed searches: redis AND postgres AND (k8s OR kubernetes) AND is_shared:true",
          type: "string",
        },
        sort: {
          description:
            "Optional. Sort dashboards by field. Valid values: title, created_at, modified_at, popularity, metadata.widget_count, metadata.is_public, metadata.public_title, metadata.shared_by.handle, metadata.status, metadata.share_type. Prefix with '-' for descending order, e.g. '-modified_at'.",
          type: "string",
        },
        start_at: {
          description:
            "Optional. Offset to start returning results from. Use this to get the next batch of results.",
          type: "integer",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
      },
      required: ["telemetry"],
      type: "object",
    },
    name: "search_datadog_dashboards",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Search Datadog Entities",
    },
    description:
      "Search Datadog entities through the entity graph, including attributes and direct relationships. Prefer this tool over searching telemetry, monitors, dashboards, or other resources when the request is about service identity, catalog ownership, dependencies, or services owned by the current user's teams. Use entity_type=service directly for ordinary service lookup and dependency questions; kind: is added by the server. Ref, type, and known type-specific identity fields are always returned, so omit fields for name-only discovery. Treat dependency requests and explicit requests for all, every, complete lists, or inventories as complete. Explicit all, every, complete-list, and inventory requests override this bounded-search rule. Treat owner- or team-filtered service searches as complete unless the user explicitly requests a sample, maximum count, or first page. For complete results, continue while METADATA.page_truncated is true, page_next_cursor remains, or a requested relationship is truncated or has next_cursor. Do not infer completion from a short page. Set owned_by_current_user_teams=true instead of inferring the user's teams from telemetry. For service ownership, request the owner field; do not invent an ownership relationship. Query supports boolean expressions such as name:chrono OR owner:idp. Use exact name:value when the user supplies a concrete internal service name; do not broaden it with wildcards. Use name:value* for prefix requests. For vague possible-match requests that do not require complete results, make one search with page.limit at most 25 using name:*value*. Present an incomplete, non-ranked candidate sample and ask the user to narrow the request; do not paginate. For a dependency question with no direction, request both upstream_services and downstream_services and keep the directions separate. Filters and relationship names are validated by the entity graph. Requested fields may be omitted, so do not treat an absent field as empty. Use owned_by_current_user_teams only when guidance for the selected entity type says it is supported.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        entity_type: {
          description: "Entity type to return.",
          enum: ["service"],
          type: "string",
        },
        fields: {
          description:
            "Additional attributes to return. Ref, type, and known identity fields are returned automatically: name and display_name for services, and name and handle for teams when that kind is enabled. The entity graph may omit unknown or inaccessible fields; do not treat an omitted field as empty.",
          items: {
            type: "string",
          },
          type: "array",
        },
        max_tokens: {
          default: 4000,
          description:
            "Approximate response token budget. The maximum is 20000.",
          maximum: 20000,
          minimum: 1,
          type: "integer",
        },
        owned_by_current_user_teams: {
          description:
            "Restrict results to entities owned by the current user's teams. Use only when guidance for the selected entity type says it is supported.",
          type: "boolean",
        },
        page: {
          additionalProperties: false,
          description:
            "Primary entity page size and opaque continuation cursor.",
          properties: {
            cursor: {
              description:
                "Opaque continuation cursor returned by a previous call. On follow-up, repeat the same entity selection, fields, relationships, and page limit, then add this cursor.",
              type: "string",
            },
            limit: {
              description:
                "Maximum primary entities to fetch. The maximum is 100.",
              type: "integer",
            },
          },
          type: "object",
        },
        query: {
          description:
            "Filter fragment without kind:. Boolean expressions and wildcards are supported. For services, use exact name:value for a concrete internal name, name:value* for prefix matching, and name:*value* for contains or ambiguous matching. Service wildcard name filters also match display_name; do not add an equivalent display_name clause. Other filters are passed to the entity graph for validation.",
          type: "string",
        },
        relationships: {
          description:
            "Direct one-hop relationships to expand with bounded projections.",
          items: {
            additionalProperties: false,
            properties: {
              cursor: {
                description:
                  "Opaque relationship continuation cursor returned by a previous call. On follow-up, repeat the same entity selection, relationship name, target kind, and limit, then add this cursor. The projected fields can change.",
                type: "string",
              },
              fields: {
                description:
                  "Additional attributes for related entities. Ref, type, and known identity fields are returned automatically. The entity graph may omit unknown or inaccessible fields. Omit fields and target_kind when identity is sufficient.",
                items: {
                  type: "string",
                },
                type: "array",
              },
              limit: {
                description:
                  "Maximum related entities returned for each primary entity. The maximum is 500.",
                type: "integer",
              },
              name: {
                description:
                  "Relationship name to expand. From the selected service's perspective, downstream_services are services it depends on, calls, or sends data to; upstream_services are services that depend on, call, or send data to it. Other names are passed to the entity graph for validation.",
                type: "string",
              },
              target_kind: {
                description:
                  "Related entity kind. Required when fields are requested unless the tool knows the relationship target. Service upstream_services and downstream_services, and team owned_services, target services automatically. For an unfamiliar relationship, use the exact target kind from its schema; do not guess.",
                type: "string",
              },
            },
            required: ["name"],
            type: "object",
          },
          type: "array",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
      },
      required: ["telemetry"],
      type: "object",
    },
    name: "search_datadog_entities",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get Events",
    },
    description:
      "Search and retrieve raw Datadog events (deployments, alerts, system activities, infrastructure changes, etc.). Do NOT use for counts, aggregations, or grouped analysis \u2014 use aggregate_events instead. Best for: inspecting individual events, reading event titles/messages/tags, and exploring event patterns before deciding how to aggregate them.\nAdd a 'source' only when the question names a kind of event:\ndeploys and other tracked changes: 'source:change_tracking', not 'source:kubernetes', which returns events but not deploys\na deployed version Datadog detected as regressing: 'source:deployment_analysis'\na monitor firing or recovering: 'source:alert'\nmonitor, downtime and mute edits: 'source:monitor_audit_event'\ncloud resource changes: 'source:resource_changes'\nAudit Trail, DORA and Synthetics results are near-absent from the feed and have their own products; tell the user rather than searching events.\nTo see which sources this org has, call aggregate_events with query '*' over 'now-7d', computes [{\"field\":\"*\",\"aggregation\":\"count\",\"output\":\"n\"}] and group_by {\"fields\":[\"source\"]}.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        from: {
          description:
            "Optional. Start time in ISO 8601 format (e.g. '2023-01-01T00:00:00Z'), Unix timestamps (milliseconds), or relative time that MUST start with 'now-' prefix (e.g. 'now-1h' 'now-15m' 'now-1d'). Defaults to 'now-1h'",
          type: "string",
        },
        max_tokens: {
          description:
            "Max tokens in the response. Default: 10000. Hard cap: 20000 (values above are clamped, not rejected).",
          type: ["integer", "string"],
        },
        query: {
          description:
            "Optional. Datadog event query.\nMatching: all terms are AND-ed, so 'kubernetes unhealthy' needs both in one event; use OR for alternatives. A bare word matches event content rather than a field, so 'kubernetes' and 'source:kubernetes' return different sets.\nEmpty results: an unknown field name and a value that never occurs both return an empty result rather than an error. Confirm a field with a wildcard first \u2014 '@monitor_id:*' matches every event carrying it.\nFields: 'status' is one of 'info', 'warn', 'error', 'ok', 'emergency', 'alert', 'critical', 'notice' or 'debug', as in 'status:(warn OR error)'. A name can exist both as a tag and as an '@' attribute, so hedge in one query: '(@monitor_id:1234 OR monitor_id:1234)'. Filter a tag by its own name, 'env:prod', not 'tags:env:prod', which fails to parse.\nExamples: 'service:my_app source:change_tracking', 'source:alert status:error'. Defaults to '*'",
          type: "string",
        },
        sort: {
          description:
            "Optional. Timestamp order only, never relevance: '-timestamp' or 'desc' for newest first, 'timestamp' or 'asc' for oldest first. Defaults to '-timestamp'",
          pattern:
            "^(-?[tT][iI][mM][eE][sS][tT][aA][mM][pP]|[aA][sS][cC]|[dD][eE][sS][cC])$",
          type: "string",
        },
        start_at: {
          description: "Offset for pagination.",
          type: ["integer", "string"],
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        to: {
          description:
            "Optional. End time in ISO 8601 format (e.g. '2023-01-01T00:00:00Z'), Unix timestamps (milliseconds), or relative time that MUST start with 'now' prefix (e.g. 'now' 'now-5m'). Defaults to 'now'",
          type: "string",
        },
      },
      required: ["telemetry"],
      type: "object",
    },
    name: "search_datadog_events",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Hosts",
    },
    description:
      "Explore Datadog hosts inventory with SQL. Queries run against the virtual 'hosts' table backed by \"dd.hosts\" (Advanced Query API), which exposes hostname, hostname_aliases (text[]; use hostname_aliases[1]), tags (hstore map; use tags->'key'), cloud_provider, resource_type, instance_type, os, os_version, agent_version, memory_mib, cpu (hstore map; use cpu->'key'), kernel (hstore map; use kernel->'key'), sources (text[]; use sources[1]), modification_detected_at (timestamp). DDSQL notes: DDSQL (Datadog SQL) is a PostgreSQL subset: every non-aggregated SELECT column must appear in GROUP BY, SELECT aliases cannot be reused in WHERE/GROUP BY/HAVING (repeat the full expression instead, e.g., GROUP BY DATE_TRUNC('hour', timestamp) not GROUP BY hour), and only declared table columns may be referenced. Use the -> operator or json_extract_path_text (cast as needed) for JSON access. Avoid unsupported constructs like ANY(), ->>, QUALIFY, information_schema, and current_timestamp. Column names containing special characters like '@' must be quoted (e.g., SELECT \"@foo\" FROM logs).",
    inputSchema: {
      additionalProperties: false,
      properties: {
        max_tokens: {
          default: 10000,
          description: "Max tokens in the response.",
          type: "number",
        },
        query: {
          default:
            "SELECT hostname, cloud_provider, resource_type, os, instance_type FROM hosts LIMIT 100",
          description:
            "SQL statement evaluated against the virtual 'hosts' table. Project columns, add WHERE clauses, group results, or order them.",
          type: "string",
        },
        start_at: {
          default: 0,
          description: "Offset for pagination.",
          type: "number",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
      },
      required: ["telemetry"],
      type: "object",
    },
    name: "search_datadog_hosts",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Incidents",
    },
    description:
      "Search Datadog incidents. Default sort: newest first. Supports filtering by state, severity, title, team, creation time, and more. Use `semantic_query` for natural-language search over AI-generated incident summaries. Prefer `query` first; only add `semantic_query` when query by keyword and metadata filters aren't sufficient to answer the question.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        from: {
          description:
            "Start of the time range. Accepts ISO 8601 (e.g. '2024-01-01T00:00:00Z' or '2024-01-01'), Unix timestamp (seconds or milliseconds), or relative (e.g. 'now-1h', 'now-7d', 'now'). Default: no filter.",
          type: "string",
        },
        max_tokens: {
          description: "Max tokens in the response. Default: 10000.",
          type: "number",
        },
        query: {
          description:
            "Search query (default: 'state:active'). Supports booleans and parentheses. Examples: 'state:active severity:SEV-1', 'state:active AND (severity:SEV-1 OR severity:SEV-2)', 'customer_impacted:true OR severity:SEV-1', '(state:active OR state:stable) AND team:backend'. Fields: state (active/stable/resolved), severity (SEV-1 through SEV-5), title, team, customer_impacted (true/false), postmortem (Yes/No), commander.handle, commander.name, responder (exact match, no wildcards), services, created (>unix_ts or [start TO end]), time_to_resolve/time_to_detect (hours), customer_impact_duration (seconds). Wildcards: 'title:*keyword*', 'field:*' (exists), '-field:*' (unset). For ongoing incidents use 'state:(active OR stable)'.",
          type: "string",
        },
        semantic_query: {
          description:
            "Semantic search over AI-generated incident summaries (root cause, system/customer impact, remediation). Matches by meaning, not exact words \u2014 e.g. 'consumer falling behind' can match 'kafka lag'. Results ranked by similarity; do not re-filter by keyword. Combinable with structured filters in `query` (state, severity, time range, commander, service). Do NOT combine with keyword search in `query` \u2014 if the user's question is keyword-shaped, use `query` alone.",
          type: "string",
        },
        sort: {
          description:
            "Sort order. Ignored with with_facets. Values: -created (default), created, -declared, declared, -resolved, resolved, -modified, modified, -severity, severity.",
          type: "string",
        },
        start_at: {
          description: "Offset for pagination.",
          type: "number",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        to: {
          description:
            "End of the time range. Accepts ISO 8601 (e.g. '2024-01-01T00:00:00Z' or '2024-01-01'), Unix timestamp (seconds or milliseconds), or relative (e.g. 'now-1h', 'now-7d', 'now'). Default: no filter.",
          type: "string",
        },
        with_facets: {
          description:
            "If true, returns facet breakdowns (counts by severity, state, etc.) instead of individual incidents.",
          type: "boolean",
        },
      },
      required: ["telemetry"],
      type: "object",
    },
    name: "search_datadog_incidents",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Search Logs",
    },
    description:
      "Search and retrieve raw log entries or log patterns. Do NOT use for counting or aggregations \u2014 use analyze_datadog_logs instead. Best for: viewing raw logs, discovering patterns (use_log_patterns=true), and discovering custom attributes via extra_fields for use as extra_columns in analyze_datadog_logs. Response shape (when extra_fields is set, output is YAML): three buckets \u2014 (1) top-level fields like env/host/service queried bare (`env:prod`); (2) `attributes:` map with source-prefixed keys \u2014 `custom.X` and `attributes.X` are queried as `@X` (strip the bucket prefix), but `error.X` keeps its prefix as `@error.X` because `error` is a real top-level attribute; (3) `tags:` list of `key:value` strings queried bare. Load the datadog/logs skill for the full key-translation table.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        clustering_pattern_field: {
          default: "message",
          description:
            "Log field used for clustering logs with similar values when use_log_patterns is true. Can cluster on any attribute or tag present in the logs. Consider '@error.message', '@errorMessage', '@error' or '@error.stack' when investigating error logs. Default: 'message'.",
          type: "string",
        },
        extra_fields: {
          description:
            "Extra attributes/tags to include. Use bare names without '@' or prefix (e.g., 'duration', not '@duration' or 'custom.duration'). Supports wildcards ('experiments*', '*' for all). The response shows attribute keys with a display prefix (custom.X, error.X); that prefix signals the translation: custom.X \u2192 @X, error.X \u2192 @error.X in extra_columns. Load the datadog/logs skill for the full translation table.",
          items: {
            type: "string",
          },
          type: "array",
        },
        from: {
          default: "now-1h",
          description:
            "Start of the time range. Accepts ISO 8601 (e.g. '2024-01-01T00:00:00Z' or '2024-01-01'), Unix timestamp (seconds or milliseconds), or relative (e.g. 'now-1h', 'now-7d', 'now'). Default: now-1h.",
          type: "string",
        },
        indexes: {
          default: ["*"],
          description:
            "Restrict to specific log indexes (e.g. ['main', 'pci']). Default: ['*'] (all).",
          items: {
            type: "string",
          },
          type: "array",
        },
        limit: {
          description:
            "Optional maximum number of logs or patterns to return (1-1000). If omitted, results are bounded only by max_tokens.",
          maximum: 1000,
          minimum: 1,
          type: "number",
        },
        max_tokens: {
          default: 5000,
          description:
            "Max tokens in the response. Hard cap: 20000 (values above are clamped, not rejected).",
          minimum: 0,
          type: "number",
        },
        pattern_group_by: {
          description:
            "Only used when use_log_patterns=true. Fields/tags to group patterns by (e.g. ['service', '@env']). Can group by any attribute or tag present in the logs. Each returned pattern will be scoped to a unique combination of these fields, and their values surface in the 'groups' column. Default: no grouping (patterns aggregate across all values).",
          items: {
            type: "string",
          },
          type: "array",
        },
        query: {
          description:
            "Datadog search query. When relevant services are known, prefer filtering by service name or prefix. Broad queries are fine for discovery or cross-service questions. Common tags: service, env, host, source. Prefix raw attributes with '@'. Examples: 'service:nginx status:error', '@http.status_code:[400 TO 499]', 'env:(production OR test) AND -version:beta', 'service:web* foo', '\"foo bar\"'. Tip: '(source:foo OR service:foo)' if unsure which field. Severe issues: 'status:(critical OR alert OR emergency)'.",
          type: "string",
        },
        sort: {
          default: "-timestamp",
          description: "Sort order. Default: -timestamp.",
          enum: ["timestamp", "-timestamp"],
          type: "string",
        },
        start_at: {
          default: 0,
          description: "Offset for pagination.",
          minimum: 0,
          type: "number",
        },
        storage_tier: {
          description:
            "Storage type. 'flex_and_indexes' (Flex + hot), 'online_archives_and_indexes' (archive + hot). Default: hot only.",
          enum: [
            "indexes",
            "flex_and_indexes",
            "flex",
            "online_archives_and_indexes",
            "online-archives",
          ],
          type: "string",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        to: {
          default: "now",
          description:
            "End of the time range. Accepts ISO 8601 (e.g. '2024-01-01T00:00:00Z' or '2024-01-01'), Unix timestamp (seconds or milliseconds), or relative (e.g. 'now-1h', 'now-7d', 'now'). Default: now.",
          type: "string",
        },
        use_log_patterns: {
          default: false,
          description:
            "When true, returns clusters of logs with similar messages instead of raw logs.",
          type: "boolean",
        },
      },
      required: ["query", "telemetry"],
      type: "object",
    },
    name: "search_datadog_logs",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Metrics",
    },
    description:
      "List available metrics in Datadog with optional filtering by name and tags. Set use_cloud_cost=true to search Cloud Cost Management metrics.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        from: {
          default: "now-2w",
          description:
            "Lookback window start. ISO 8601, Unix timestamp, or relative ('now-2w', 'now-30d'). Max: 30 days.",
          type: "string",
        },
        include_top_tag_keys: {
          description:
            "Deprecated: ignored. Do not set. Retained as a compatibility tombstone so callers with a cached tool schema do not fail validation; results are always bare metric names.",
          type: "boolean",
        },
        max_tokens: {
          default: 10000,
          description: "Max tokens in the response.",
          type: "number",
        },
        name_filter: {
          description:
            "Keyword search on metric name segments. For regular metrics, uses an OR policy: any matching token contributes to recall, ranked by how many tokens match. Behaves differently under use_cloud_cost=true; see that parameter.",
          type: "string",
        },
        percentiles_filter: {
          description:
            "Set to 'enabled_only' to restrict results to metrics that have percentile aggregation enabled. Omit it to apply no percentile filter, which is almost always what you want \u2014 'enabled_only' hides every metric that does not have percentile aggregation enabled, so use it only when the task is specifically about percentiles.",
          enum: ["enabled_only"],
          type: "string",
        },
        tag_filter: {
          description:
            "Filter by tags within lookback window. Supports wildcards, AND, OR. Examples: 'service:redis* AND host:prod-1', 'service:redis OR service:postgres'.",
          type: "string",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        use_cloud_cost: {
          description:
            "Set true to search Cloud Cost Management metrics instead of regular metrics. Cloud cost metric search only returns metrics enabled for the organization's configured CCM sources. tag_filter and percentiles_filter=enabled_only are not supported for cloud cost metric search. name_filter also behaves differently here: it matches if the filter is a substring of the metric name (case-insensitive), or a glob match if the filter contains * or ? \u2014 not the OR/token-ranking behavior used for regular metrics.",
          type: "boolean",
        },
      },
      required: ["telemetry"],
      type: "object",
    },
    name: "search_datadog_metrics",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Get Monitors",
    },
    description:
      "List and retrieve information about Datadog monitors. This tool helps discover monitors, their status, configuration, and alerts. Use this tool when you need to find monitors for investigation, management, or analysis purposes.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        include_tags: {
          description:
            "Optional. List of monitor tag patterns to include in the response. Supports wildcards: 'env*' matches 'env:prod' and 'env:staging'; '*' includes all tags. If not provided, no tags are returned \u2014 monitors can carry many tags, so opt in only to the ones you need.",
          items: {
            type: "string",
          },
          type: "array",
        },
        max_tokens: {
          description: "Max tokens in the response. Default: 10000.",
          type: "number",
        },
        query: {
          description:
            "Optional. Search query string. Supports filtering by id, title, status, team, priority, tag, etc. Examples: 'title:my-monitor', 'id:167097893', 'env:prod', 'status:alert muted:true', 'tag:\"monitor_gate:true\"', 'notification:slack-logs-team-ops', 'priority:p2 AND (title:my-monitor OR title:my-other-monitor)'",
          type: "string",
        },
        sort: {
          description:
            "Optional. Sort monitors by field. Valid values: 'title', 'status', 'tags'. Use a bare field name for ascending order (e.g. 'status') or prefix with '-' for descending order (e.g. '-status').",
          type: "string",
        },
        start_at: {
          description: "Offset for pagination.",
          type: "number",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
      },
      required: ["telemetry"],
      type: "object",
    },
    name: "search_datadog_monitors",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Search Notebooks",
    },
    description:
      "Search Datadog notebooks. Use this tool when you need to find notebooks for investigation or monitoring purposes. Results can be filtered, for example by author, and sorted. This tool returns snippets of the original notebook. To fetch the entire notebook, use the get_datadog_notebook tool.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        count: {
          default: 20,
          description:
            "Optional. Maximum number of notebook results to return.",
          maximum: 100,
          minimum: 1,
          type: "number",
        },
        filter: {
          default: "",
          description:
            'Optional. Filter results by one or more of the following fields: author.handle, author, type, tags, metadata.has_computational_cells, modified_from, modified_to, id, dataset_id, experience_type, deleted, show_favorites. For example, to find all notebooks by author "abc@test.com", use: author.handle:abc@test.com and leave query empty. Multiple filters can be space-separated, e.g. "type:runbook author.handle:abc@test.com".',
          type: "string",
        },
        include_facets: {
          default: false,
          description:
            "Optional. When true, the response includes facet aggregations (e.g. tags, author, type, template_variables) in the metadata. Useful for understanding the distribution of notebooks before drilling into specific searches.",
          type: "boolean",
        },
        max_tokens: {
          default: 10000,
          description: "Max tokens in the response.",
          type: "number",
        },
        query: {
          default: "",
          description:
            "Optional. Keywords matched against notebook names and text content. Leave empty to list all notebooks.",
          type: "string",
        },
        sort: {
          description:
            "Optional. Sort notebooks by field. Valid values: name, created_at, modified_at, deleted_at, favorited_by. Prefix with '-' for descending order, e.g. '-modified_at'. Omit to rank results by your favorites, then relevance.",
          type: "string",
        },
        start_at: {
          default: 0,
          description: "Offset for pagination.",
          type: "number",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
      },
      required: ["telemetry"],
      type: "object",
    },
    name: "search_datadog_notebooks",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "Search RUM Events",
    },
    description:
      "Search and retrieve raw Datadog RUM events using advanced query syntax. Do NOT use for counts, aggregations, or grouped analysis. Best for: inspecting individual RUM events, debugging specific user experience issues, exploring available event attributes in raw or detailed_output responses before deciding what to aggregate. Supports filtering by event types (session, view, action, error, resource, long_task, vital, operation), user attributes, performance metrics, and more. Results include detailed event data for analysis.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        detailed_output: {
          description:
            "Optional. If true, returns complete event data including all attributes, performance metrics, and metadata. If false, returns summarized events with key fields only (faster, smaller response). Default is false",
          type: "boolean",
        },
        from: {
          description:
            "Optional. Start time in ISO 8601 format (e.g. '2023-01-01T00:00:00Z'), Unix timestamps (milliseconds), or relative format starting with `now-` (e.g. `now-15m`, `now-1h`, `now-1d`, `now-7d`, `now-30d`). Defaults to now-7d",
          type: "string",
        },
        limit: {
          description:
            "Optional. Maximum number of events to return (1-5000). Use this to reliably fetch a specific number of results, e.g. limit:1 to get the single most recent matching event. If omitted, results are only bounded by max_tokens.",
          maximum: 5000,
          minimum: 1,
          type: "number",
        },
        max_tokens: {
          description:
            "Optional. Maximum number of tokens to include in the response. Use higher values (50000+) for comprehensive analysis, lower values (5000-10000) for quick overviews. Default is 10000",
          type: "number",
        },
        query: {
          description:
            'Required. Search query following Datadog syntax. RUM timing fields (e.g. `@view.loading_time`, `@action.loading_time`, `@resource.duration`) are in nanoseconds (1s = 1e9). Common patterns: `@type:error` (errors only), `@type:view @view.loading_time:>5000000000` (slow page loads, >5s), `@type:operation @operation.status:failure` (failed operations), `@application.name:"My App" status:error` (app-specific errors), `@user.id:123` (specific user), `@view.url:*/checkout/*` (checkout pages), `@browser.name:Chrome` (browser-specific), `env:production` (environment). Use @ prefix for attributes, quotes for spaces, wildcards (*), ranges ([100 TO 500]), and boolean operators (AND, OR, NOT). IMPORTANT: free-text search uses full-word matching \u2014 dots (.) are NOT word separators (`foo.bar` is one token, `bar` alone matches nothing). Wrap partial terms in wildcards or target the attribute: `@error.message:*bar*`.',
          type: "string",
        },
        start_at: {
          description: "Offset for pagination.",
          type: "number",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        to: {
          description:
            "Optional. End time in ISO 8601 format (e.g. '2023-01-01T00:00:00Z'), Unix timestamps (milliseconds), or relative format starting with `now` (e.g. `now`, `now-5m`). Defaults to now",
          type: "string",
        },
      },
      required: ["query", "telemetry"],
      type: "object",
    },
    name: "search_datadog_rum_events",
  },
  {
    annotations: {
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
      readOnlyHint: true,
      title: "List Spans",
    },
    description:
      "Retrieve raw Datadog APM spans matching a query. Do NOT use for counts, aggregations, or grouped analysis \u2014 use aggregate_spans instead. Best for: inspecting individual spans, debugging request flows and failures, and discovering span fields/attributes, including via custom_attributes, that you may want to aggregate or group by with aggregate_spans. IMPORTANT: When the user asks for 'security traces', 'AppSec traces', 'AAP traces', 'security activity', or 'security signals on traces', the query MUST start from `@appsec.security_activity:*`. Do NOT substitute keyword-based guesses like `service:*security*`, `resource_name:*auth*`, `@http.status_code:401`, etc. \u2014 those do not identify security traces. Application & API Protection (AAP) spans carry these standard attributes (all strings): `@appsec.security_activity` (`<category>.<type>`, e.g. `attack_attempt.sql_injection`, `business_logic.users.login.failure`; multi-valued when multiple rules match); `@appsec.category` (top-level classification, e.g. `attack_attempt`, `business_logic`); `@appsec.type` (specific threat/event type, e.g. `sql_injection`, `xss`, `users.login.failure`); `@appsec.rule_id` (AAP rule that matched, e.g. `crs-942-100`; multi-valued); `@appsec.blocked` (`true`/`false`, whether AAP blocked the request). Use these attributes to narrow AppSec queries (e.g. `@appsec.category:attack_attempt`, `@appsec.type:sql_injection`, `@appsec.blocked:true`). The response includes a traces_explorer_url for the overall query. To link to an individual trace in the Datadog UI, use the pattern: <base_url>/apm/trace/<trace_id> (e.g. https://app.datadoghq.com/apm/trace/abc123). The base_url for the user's org is available in the response metadata.",
    inputSchema: {
      additionalProperties: false,
      properties: {
        cursor: {
          description:
            "Optional. Pagination cursor from a previous response's next_cursor, used to fetch the next page.",
          type: "string",
        },
        custom_attributes: {
          description:
            "Optional. When omitted or empty, all custom attributes and tags are returned. When provided, limits custom attributes and tags to the default set (error.*, db.*, http.*, git.*, peer.*, issue.id, appsec.*), the organization's primary tags, attributes referenced as @facets in the query, and the supplied patterns. Supports wildcards (e.g. 'experiments*' includes all attributes/tags starting with 'experiments') and nested paths (e.g. 'issue.id').",
          items: {
            type: "string",
          },
          type: "array",
        },
        from: {
          description:
            "Start of the time range. Accepts ISO 8601 (e.g. '2024-01-01T00:00:00Z' or '2024-01-01'), Unix timestamp (seconds or milliseconds), or relative (e.g. 'now-1h', 'now-7d', 'now'). Default: now-1h.",
          type: "string",
        },
        limit: {
          description:
            "Optional. Maximum number of spans to return (1-1000). Use this to reliably fetch a specific number of results, e.g. limit:1 with the default sort to get the single most recent matching span. If omitted, results are only bounded by max_tokens.",
          maximum: 1000,
          minimum: 1,
          type: "number",
        },
        max_tokens: {
          description:
            "Max tokens in the response. Default: 10000. Hard cap: 20000 (values above are clamped, not rejected).",
          type: "number",
        },
        query: {
          description:
            "Required. Query string, supports boolean operators (if not specified, AND is assumed) and parentheses. To search for tags with multiple values, use OR; for example `type:(http OR rpc)`. Spaces should be escaped to allow regex expressions to work correctly; for example `@error.message:remote\\ connection\\ *`. Durations should be specified in nanoseconds; for example, to search for spans with a duration greater than 5ms, use `@duration:>5000000`. For 'security traces' / AppSec / AAP requests, the query MUST include `@appsec.security_activity:*` (AND-ed with any other user-specified filters); refine with `@appsec.category`, `@appsec.type`, `@appsec.rule_id`, or `@appsec.blocked` when the user specifies a class of threat, rule, or block status. Examples: `service:nginx status:error`, `trace_id:7d5d747be160e280504c099d984bcfe0`, `status:error AND service:(foo OR bar)`, `@appsec.security_activity:* service:payments`, `@appsec.security_activity:* @appsec.category:attack_attempt @appsec.blocked:true`",
          type: "string",
        },
        sort: {
          description: "Optional. Sort order for spans. Default is -timestamp",
          type: "string",
        },
        telemetry: {
          additionalProperties: false,
          properties: {
            intent: {
              description:
                "Briefly describe the wider context task, and why this tool was chosen. Omit argument values, PII/secrets. Use English.",
              type: "string",
            },
          },
          required: ["intent"],
          type: "object",
        },
        to: {
          description:
            "End of the time range. Accepts ISO 8601 (e.g. '2024-01-01T00:00:00Z' or '2024-01-01'), Unix timestamp (seconds or milliseconds), or relative (e.g. 'now-1h', 'now-7d', 'now'). Default: now.",
          type: "string",
        },
      },
      required: ["query", "telemetry"],
      type: "object",
    },
    name: "search_datadog_spans",
  },
];
